import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Skeleton } from "antd";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import "@/assets/css/store-carousel.css";

// import required modules
import { Grid, Pagination } from "swiper/modules";

import "@/assets/css/swiper-cards.css";

import {
  useListaProductosDestacados,
  useListaComunasYMaquinasPorProductoNombre,
} from "@/hooks/useProductos";
import { useListaEmpresasMutation } from "@/hooks/useEmpresas";
import { formatMoney } from "@/lib";

import { StoresSwiper } from "./StoresSwiper";
import { ModalProductDetail } from "./components/ModalProductDetail";

export const Featured = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const empresas = useListaEmpresasMutation();
  const { mutateAsync: fetchEmpresas } = empresas;
  const productos = useListaProductosDestacados();
  const { mutateAsync: mutateAsyncMaquinas } =
    useListaComunasYMaquinasPorProductoNombre();
  const [allProducts, setAllProducts] = useState([]); // Almacena todos los productos cargados
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedTiendaId, setSelectedTiendaId] = useState(null);
  const [maquinas, setMaquinas] = useState([]);
  const [isLoadingMaquinas, setIsLoadingMaquinas] = useState(false);
  const itemRef = useRef(null);
  const storeRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  // ⭐️ 1. Preparar la lista con la distancia calculada y ordenar
  const productosOrdenados = useMemo(() => {
    if (!productos.data || !Array.isArray(productos.data)) return [];

    const productosDetalleCompletos = [];

    productos.data.forEach((productoPadre) => {
      if (productoPadre.detalle && Array.isArray(productoPadre.detalle)) {
        productoPadre.detalle
          .filter((prodDetalle) => prodDetalle.vende_licor !== 1) // si quieres filtrar, opcional
          .forEach((prodDetalle) => {
            productosDetalleCompletos.push({
              ...prodDetalle,
              // Inclusión de propiedades del padre
              empresa_id: productoPadre.empresa_id,
              empresa_nombre: productoPadre.empresa_nombre,
              maquina_id: productoPadre.maquina_id,
              maquina_nombre: productoPadre.maquina_nombre,
              maquina_direccion: productoPadre.maquina_direccion,
              maquina_latitud: productoPadre.maquina_latitud,
              maquina_longitud: productoPadre.maquina_longitud,
              distancia: productoPadre._distancia,
            });
          });
      }
    });

    return productosDetalleCompletos;
  }, [productos.data]);

  useEffect(() => {
    if (productos.data) {
      // Si hay datos, actualiza 'allProducts' con los datos obtenidos.
      setAllProducts(productosOrdenados);

      // Si no hay un filtro de tienda seleccionado, inicializa 'filteredItems' con todos los productos
      if (selectedTiendaId === null) {
        setFilteredItems(productosOrdenados);
      }
    }
  }, [productos.data, productosOrdenados, selectedTiendaId]);

  useEffect(() => {
    if (selectedTiendaId !== null) {
      // Si hay una tienda seleccionada
      const filteredList = allProducts.filter(
        (item) => item.empresa_id === selectedTiendaId,
      );
      setFilteredItems(filteredList);
    } else {
      // Si no hay tienda seleccionada (o se reseteó), se muestran todos los productos
      setFilteredItems(allProducts);
    }
  }, [allProducts, selectedTiendaId]);

  const handleClick = async (item) => {
    const nombre = item?.producto_nombre ?? item?.detalle?.[0]?.producto_nombre;
    if (!item || !nombre) return;
    const requestId = ++requestIdRef.current;
    itemRef.current = item;
    storeRef.current =
      _.find(Array.isArray(empresas.data) ? empresas.data : [], {
        empresa_id: item.empresa_id,
      }) ?? null;
    setMaquinas([]);
    setIsLoadingMaquinas(true);
    setShow(true);
    try {
      const resp = await mutateAsyncMaquinas(nombre);
      if (requestIdRef.current !== requestId) return;
      setMaquinas(Array.isArray(resp) ? resp : []);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      Sentry.captureException(err);
      setMaquinas(null);
    } finally {
      if (requestIdRef.current === requestId) setIsLoadingMaquinas(false);
    }
  };

  const handleClickTienda = (idTienda) => {
    setSelectedTiendaId(idTienda);
  };

  const goToHome = () => {
    const homePath = localStorage.getItem("companyHome") || "/";
    navigate(homePath);
  };

  const FeaturedTitle = () => {
    return (
      <div className="featured-title" onClick={goToHome}>
        <i className="arrow-left" />
        <h2>Destacados</h2>
      </div>
    );
  };

  const FeaturedSubtitle = () => {
    return (
      <div className="featured-subtitle">
        <h2>Filtrar por tienda</h2>
      </div>
    );
  };

  const ProductCard = ({ item }) => {
    return (
      <div key={item.producto_id} className="card-product-carousel">
        <div className="card-img-product" onClick={() => handleClick(item)}>
          <img src={item.producto_foto} alt={item.producto_id} />
        </div>

        {/* <ButtonLike /> */}

        <div className="card-content-product">
          <h2>{item.producto_nombre}</h2>
          <p className="price-product">
            {" "}
            {formatMoney(item.producto_precio)} CLP
          </p>
          <div className="location-product">
            <i className="icon-location"></i>
            <span className="text-location">{item.maquina_direccion}</span>
          </div>
        </div>
      </div>
    );
  };

  const SwiperStorageAvailable = () => {
    if (_.size(filteredItems) > 0) {
      if (_.size(filteredItems) > 3) {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 3,
              fill: "row",
            }}
            spaceBetween={10}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 42,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(filteredItems) > 0 ? (
              filteredItems.map((product) => {
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      key={product.producto_id}
                      item={{ ...product, like: false }}
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      } else {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 1,
              fill: "row",
            }}
            spaceBetween={5}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 1,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(filteredItems) > 0 ? (
              filteredItems.map((product) => {
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      key={product.producto_id}
                      item={{ ...product, like: false }}
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      }
    } else {
      return (
        <h4 className="text-center prompt-bold text-black mb-3">
          No se encontraron productos disponibles.
        </h4>
      );
    }
  };

  return (
    <div className="container my-5" style={{ flexGrow: 1 }}>
      {show && (
        <ModalProductDetail
          show={show}
          setShow={setShow}
          item={itemRef}
          store={storeRef}
          machines={maquinas}
          isLoading={isLoadingMaquinas}
        />
      )}
      <FeaturedTitle />
      <FeaturedSubtitle />
      <div className="row">
        <div className="col-12">
          <StoresSwiper handleClickTienda={handleClickTienda} />
        </div>
        <div className="col-12">
          {/* <Skeleton loading={productos} active> */}
          {/* <StoreCarousel /> */}
          <SwiperStorageAvailable />
          {/* </Skeleton> */}
        </div>
      </div>
    </div>
  );
};
